import { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation, useParams, Link, NavLink } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useGmailComposeUrl } from "@/lib/mail";
import { useContactTracker, lastContactedLabel, isContactStale } from "@/lib/contactTracker";
import ContactConfirmBanner from "@/components/ContactConfirmBanner";
import ContactDot from "@/components/ContactDot";
import BillingDot from "@/components/BillingDot";
import AssessmentInProgressBanner from "@/components/AssessmentInProgressBanner";
import BillingAlertBanner from "@/components/BillingAlertBanner";
import { TodayAttendanceTile } from "@/pages/coach/TodayAttendance";
import LeadOutreachTracker from "@/components/coach/LeadOutreachTracker";
import LeadProfileMetaEditor from "@/components/coach/LeadProfileMetaEditor";
import AthleteNameLink from "@/components/coach/AthleteNameLink";
import ChargeClientModal, { PackBalancesWidget } from "@/components/coach/ChargeClientModal";
import LeadFunnelBar from "@/components/coach/LeadFunnelBar";
import RescheduleAssessmentModal from "@/components/coach/RescheduleAssessmentModal";
import ScheduleAssessmentModal from "@/components/coach/ScheduleAssessmentModal";
import { formatTimeDisplay } from "@/lib/schedule";
import EnrollmentLinkModal from "@/components/coach/EnrollmentLinkModal";
import SubscriptionStatusChip from "@/components/coach/SubscriptionStatusChip";
// 2026-02-23 (Phase 2b cleanup): StartSubscriptionButton + PackPricingMatrix
// imports removed — coaches now charge & subscribe from pricing blocks via
// the Billing tab on the lead detail panel.
import QuickAddClientModal from "@/components/coach/QuickAddClientModal";
import OwnerUserAdminModal from "@/components/coach/OwnerUserAdminModal";
import DemoClientPanel from "@/components/coach/DemoClientPanel";
import StripeModeBanner from "@/components/coach/StripeModeBanner";
import ReferralSourcePanel from "@/components/coach/ReferralSourcePanel";
import AthleteEditorModal from "@/components/profile/AthleteEditorModal";
import LeadDetailSchedule from "@/components/coach/leadDetail/Schedule";
import LeadDetailBilling from "@/components/coach/leadDetail/Billing";
import SendPricingButton from "@/components/coach/leadDetail/SendPricingButton";
import SectionErrorBoundary from "@/components/coach/SectionErrorBoundary";
import UpcomingRenewalsPanel from "@/components/coach/UpcomingRenewalsPanel";
import CoverageDot from "@/components/coach/leadDetail/CoverageDot";
import AthleteSubscribeWidget from "@/components/coach/leadDetail/AthleteSubscribeWidget";
import MemberCounter from "@/components/coach/MemberCounter";
import RecurringRevenueWidget from "@/components/coach/RecurringRevenueWidget";
import MembersWithoutRecurringTile from "@/components/coach/MembersWithoutRecurringTile";
import UnifiedTimeline from "@/components/coach/UnifiedTimeline";
import {
    LeadReplyControls,
    IntakeSubmissionPanel,
    CommunicationLogPanel,
    WaiverPanel,
} from "@/components/coach/leadDetail/Info";
import {
    Search,
    Filter,
    Loader2,
    Users,
    User,
    UserCheck,
    UserX,
    Phone,
    Mail,
    MapPin,
    Heart,
    FileSignature,
    ChevronRight,
    ChevronLeft,
    UserCog,
    BadgeCheck,
    Layers,
    Calendar,
    CheckCircle2,
    Megaphone,
    Activity,
    UserMinus,
    ClipboardList,
    CreditCard,
    DollarSign,
    Briefcase,
    LogOut,
    MessageCircle,
    BarChart3,
    MessageSquare,
    Trash2,
    ArrowLeft,
    Users2,
    Pencil,
    Plus,
    Wallet,
    ChevronDown,
    CalendarDays,
    Zap,
    AlertCircle,
    AlertTriangle,
    ShieldCheck,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { STATUS_META, TRACK_LABELS, calcAge } from "@/lib/permissions";
import AssessmentFormModal from "@/components/coach/AssessmentFormModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FILTERS = [
    // "All" removed — Sachin spec 2026-02-17: keep just Leads / Members /
    // Non-Members so the roster has a clear purpose at every tap.
    // Inactive stays accessible via the dropdown picker on each row.
    { key: "lead", label: "Leads" },
    { key: "prospect", label: "Prospects" },
    { key: "assessed", label: "Assessed" },
    { key: "member", label: "Members" },
    { key: "non_member", label: "Non-Members" },
    // Sachin 2026-08: "members → inactive seems to remove the profile."
    // Nothing was deleted — inactive people just had NO tab to live in.
    { key: "inactive", label: "Inactive" },
];

// Statuses where the contextual "+ Add" button shows up + its label.
const QUICK_ADD_CONFIG = {
    lead:       { label: "+ Add Lead",       target_status: "lead" },          // sidebar Easy Access → /coach?quick_add=lead
    member:     { label: "+ Add Member",     target_status: "member" },        // owner intent: invite-flow lands them in Members immediately
    non_member: { label: "+ Add Non-Member", target_status: "non_member" },
};

/**
 * LastInBadge — tiny chip showing "Last in: 3d" / "⚠ 12d" / "Never".
 *
 * Color thresholds (calendar days since most-recent checked_in booking):
 *   • ≤ 7d  → zinc (normal)
 *   • 8-14d → amber (slipping)
 *   • > 14d → rose (likely churning)
 *   • null  → muted zinc, "Never"
 */
// Helper hoisted OUT of the render tree so the eslint React-Compiler
// purity rule doesn't flag the `Date.now()` call inside a hook.
function _daysSinceIso(ts) {
    const then = new Date(ts).getTime();
    if (Number.isNaN(then)) return 0;
    return Math.max(0, Math.floor((Date.now() - then) / 86400000));
}

function LastInBadge({ ts, lead, onOverride }) {
    // Sachin 2026-02-28: editable Last-In date. Clicking the chip pops
    // a compact date-picker (native `<input type="date">`) with Save +
    // Clear + Cancel. The server treats override_at as the source of
    // truth once set; clearing restores the computed value.
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const isOverride = !!lead?.last_attended_override_at;

    const startEditing = (e) => {
        e?.stopPropagation();
        e?.preventDefault();
        setEditing(true);
    };

    if (editing) {
        return (
            <LastInEditor
                initial={ts}
                canClear={isOverride}
                saving={saving}
                onCancel={(e) => { e?.stopPropagation(); setEditing(false); }}
                onSave={async (isoDate) => {
                    setSaving(true);
                    try {
                        await onOverride(isoDate);
                        setEditing(false);
                    } finally {
                        setSaving(false);
                    }
                }}
            />
        );
    }

    if (!ts) {
        return (
            <button
                type="button"
                onClick={startEditing}
                data-testid="last-in-badge-never"
                className="text-[10px] tracking-wider uppercase text-zinc-600 hover:text-zinc-300 underline decoration-dotted underline-offset-2"
                title="Click to set a Last-In date"
            >
                · Never in
            </button>
        );
    }
    return <LastInBadgeInner ts={ts} onClick={startEditing} isOverride={isOverride} />;
}

function LastInBadgeInner({ ts, onClick, isOverride }) {
    const days = _daysSinceIso(ts);
    const color = days > 14 ? "text-rose-400" : days > 7 ? "text-amber-400" : "text-zinc-400";
    const warn = days > 14;
    const label = days === 0 ? "Today" : days === 1 ? "1d ago" : `${days}d ago`;
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={`last-in-badge-${days}d`}
            className={`text-[10px] tracking-wider uppercase ${color} hover:brightness-125 underline decoration-dotted underline-offset-2 cursor-pointer`}
            title={
                (isOverride ? "COACH-EDITED · " : "") +
                new Date(ts).toLocaleString() +
                " · click to change"
            }
        >
            · {warn ? "⚠ " : ""}{isOverride ? "✎ " : ""}Last in: {label}
        </button>
    );
}

/** Compact inline editor for the Last-In date. Uses a native date input
 *  so mobile gets the OS date wheel and desktop the calendar dropdown.
 *  Sachin 2026-02-28 — server persists to `last_attended_override_at`. */
function LastInEditor({ initial, canClear, saving, onCancel, onSave }) {
    const initDate = (() => {
        if (!initial) return "";
        const d = new Date(initial);
        if (Number.isNaN(d.getTime())) return "";
        // YYYY-MM-DD in the user's local timezone
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${y}-${m}-${day}`;
    })();
    const [value, setValue] = useState(initDate);
    const todayIso = new Date().toISOString().slice(0, 10);

    return (
        <span
            data-testid="last-in-editor"
            className="inline-flex items-center gap-1"
            onClick={(e) => e.stopPropagation()}
        >
            <input
                type="date"
                value={value}
                max={todayIso}
                onChange={(e) => setValue(e.target.value)}
                data-testid="last-in-editor-input"
                className="text-[10px] bg-black border border-zinc-700 text-zinc-200 rounded px-1.5 py-0.5 focus:border-brand-red focus:outline-none"
                autoFocus
            />
            <button
                type="button"
                data-testid="last-in-editor-save"
                onClick={() => value && onSave(value)}
                disabled={!value || saving}
                className="text-[10px] tracking-wider uppercase text-emerald-300 hover:text-emerald-100 disabled:opacity-40"
                title="Save"
            >
                {saving ? "…" : "Save"}
            </button>
            {canClear && (
                <button
                    type="button"
                    data-testid="last-in-editor-clear"
                    onClick={() => onSave(null)}
                    disabled={saving}
                    className="text-[10px] tracking-wider uppercase text-amber-300 hover:text-amber-100 disabled:opacity-40"
                    title="Clear override — revert to auto"
                >
                    Clear
                </button>
            )}
            <button
                type="button"
                data-testid="last-in-editor-cancel"
                onClick={onCancel}
                disabled={saving}
                className="text-[10px] tracking-wider uppercase text-zinc-500 hover:text-zinc-300 disabled:opacity-40"
                title="Cancel"
            >
                ✕
            </button>
        </span>
    );
}

/**
 * StalePill — red "⚠ Stale" chip shown on lead rows when:
 *   • lead status == "lead"
 *   • assessment scheduling email was sent ≥ 48h ago
 *   • coach hasn't manually marked them as replied
 *
 * Backend pre-computes `stale_lead`. Renders nothing when false.
 */
function StalePill({ lead, compact = false }) {
    if (!lead?.stale_lead) return null;
    const sentAt = lead.assessment_email_sent_at;
    const daysSince = sentAt ? _daysSinceIso(sentAt) : 0;
    const label = compact ? "Stale" : `Stale · no reply ${daysSince}d`;
    return (
        <span
            data-testid={`stale-lead-pill-${lead.user_id}`}
            title={`Assessment email sent ${daysSince}d ago — parent hasn${"\u2019"}t replied yet. Click ${"\u201C"}Mark replied${"\u201D"} on the lead detail panel once they respond.`}
            className="inline-flex items-center gap-1 text-[10px] tracking-[0.2em] uppercase border border-rose-600/80 bg-rose-950/40 text-rose-300 px-2 py-0.5 font-semibold"
        >
            ⚠ {label}
        </span>
    );
}

/**
 * RepliedPill — green pill confirming the coach marked the lead as replied.
 * Pairs with StalePill — only one renders at a time on the row.
 */
function RepliedPill({ lead }) {
    if (!lead?.parent_replied_at) return null;
    const days = _daysSinceIso(lead.parent_replied_at);
    const label = days === 0 ? "today" : days === 1 ? "1d ago" : `${days}d ago`;
    const title = `Marked replied by ${lead.parent_replied_by_name || "—"}`;
    return (
        <span
            data-testid={`replied-pill-${lead.user_id}`}
            title={title}
            className="inline-flex items-center gap-1 text-[10px] tracking-[0.2em] uppercase border border-emerald-600/70 bg-emerald-900/20 text-emerald-300 px-2 py-0.5"
        >
            ✓ Replied {label}
        </span>
    );
}


/**
 * RosterExportButtons — two CSV download buttons in the roster header
 * (Sachin 2026-02-28: "Let's do a leads and a member list both put into
 * a CSV. Separate lists. Push button to download.")
 *
 * Hits the backend `/api/coach/exports/{leads|members}.csv` endpoints
 * via fetch (so the browser respects the `Content-Disposition: attachment`
 * header). We fetch as a blob and synthesize a click on a hidden link
 * — that's how you trigger a programmatic download with cookie-auth.
 */
function RosterExportButtons() {
    const [busy, setBusy] = useState(null);

    const downloadCsv = async (kind) => {
        setBusy(kind);
        try {
            const r = await fetch(`${API}/coach/exports/${kind}.csv`, {
                credentials: "include",
                headers: { Accept: "text/csv" },
            });
            if (!r.ok) {
                const t = await r.text();
                throw new Error(t || `Server returned ${r.status}`);
            }
            const blob = await r.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            const today = new Date().toISOString().slice(0, 10);
            a.download = `${kind}_${today}.csv`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            toast.success(`${kind === "leads" ? "Leads" : "Members"} exported`);
        } catch (e) {
            toast.error("Export failed", { description: String(e?.message || e).slice(0, 200) });
        } finally {
            setBusy(null);
        }
    };

    const btnCls = "inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase border border-zinc-800 text-zinc-200 hover:border-brand-red hover:text-white px-3 py-2 transition-colors whitespace-nowrap shrink-0 disabled:opacity-50";

    return (
        <>
            <button
                type="button"
                data-testid="export-leads-csv"
                onClick={() => downloadCsv("leads")}
                disabled={busy !== null}
                className={btnCls}
                title="Download all leads as CSV"
            >
                {busy === "leads" ? "…" : "↓"} Export leads
            </button>
            <button
                type="button"
                data-testid="export-members-csv"
                onClick={() => downloadCsv("members")}
                disabled={busy !== null}
                className={btnCls}
                title="Download all members as CSV (incl. sub state, comp, packs, lifetime revenue)"
            >
                {busy === "members" ? "…" : "↓"} Export members
            </button>
        </>
    );
}



export default function CoachDashboard() {
    const { user, loading, logout } = useAuth();
    const navigate = useNavigate();
    const composeUrl = useGmailComposeUrl();
    const contactTracker = useContactTracker();
    const [leads, setLeads] = useState([]);
    const [perms, setPerms] = useState({});
    const [loadingLeads, setLoadingLeads] = useState(true);
    // Default to "lead" since "All" was removed (Sachin spec 2026-02-17) —
    // most opens of the dashboard are for fresh-lead triage.
    const [statusFilter, setStatusFilter] = useState("lead");
    const [sortBy, setSortBy] = useState("created"); // "created" | "recent_attend" | "stale" | "stale_leads"
    const [search, setSearch] = useState("");
    const [activeId, setActiveId] = useState(null);
    const [activeDetail, setActiveDetail] = useState(null);
    const [savingStatus, setSavingStatus] = useState(null);
    const [outreachSummary, setOutreachSummary] = useState({}); // {user_id: {call,text,email}}
    const [coaches, setCoaches] = useState([]);
    const [quickAddOpen, setQuickAddOpen] = useState(null);  // null | "lead" | "member" | "non_member"
    const location = useLocation();
    const { userId: routeUserId } = useParams();
    // Deep-link support: /coach/leads/:user_id renders this page with the
    // lead's detail panel already open. Fixes the "click → black page" bug
    // (2026-02-17) where MembersWithoutRecurringTile, RenewalHealthWidget,
    // and other tiles linked to /coach/leads/:user_id but no React route
    // existed — falling through to a blank 404.
    useEffect(() => {
        if (!routeUserId) return;
        // Fire the same handleSelect flow used by clicking the roster row.
        (async () => {
            setActiveId(routeUserId);
            setActiveDetail(null);
            try {
                const r = await axios.get(`${API}/coach/leads/${routeUserId}`, { withCredentials: true });
                setActiveDetail(r.data);
            } catch (e) {
                toast.error(e?.response?.data?.detail || "Couldn't load this client");
                setActiveId(null);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [routeUserId]);

    // ?quick_add=lead in the URL opens the Quick Add modal (used by the sidebar
    // Easy Access "+ Add a Lead" button so the user can land here from any
    // page — including same-route NavLink clicks — with the modal already on
    // screen). Strips the param after open. We key on location.search instead
    // of [] so same-route NavLink clicks (Roster → /coach?quick_add=lead) also
    // fire the effect — React Router doesn't remount on search-only changes.
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const flavor = params.get("quick_add");
        if (flavor && QUICK_ADD_CONFIG[flavor]) {
            setQuickAddOpen(flavor);
            params.delete("quick_add");
            const qs = params.toString();
            navigate(location.pathname + (qs ? `?${qs}` : ""), { replace: true });
        }
    }, [location.search, location.pathname, navigate]);

    // Derived: sorted view of leads based on the sortBy dropdown.
    const staleCount = useMemo(() => leads.filter((l) => l.stale_lead).length, [leads]);
    const [showStaleOnly, setShowStaleOnly] = useState(false);
    // "No-sub members" filter — Sachin spec 2026-02-17: shows only members
    // who are NOT currently on a recurring Stripe subscription, so the owner
    // can clear the billing leaks in one pass.
    const [showNoSubOnly, setShowNoSubOnly] = useState(false);
    const sortedLeads = useMemo(() => {
        const arr = [...leads];
        if (sortBy === "recent_attend") {
            // Most recent attendance first; rows with no attendance sink to bottom.
            arr.sort((a, b) => {
                const at = a.last_attended_at || "";
                const bt = b.last_attended_at || "";
                if (at && !bt) return -1;
                if (!at && bt) return 1;
                return bt.localeCompare(at);
            });
        } else if (sortBy === "stale") {
            // Inverse: oldest attendance first (most likely to be churning).
            // Never-attended rows go LAST so the focus stays on real lapses.
            arr.sort((a, b) => {
                const at = a.last_attended_at || "";
                const bt = b.last_attended_at || "";
                if (at && !bt) return -1;
                if (!at && bt) return 1;
                return at.localeCompare(bt);
            });
        } else if (sortBy === "stale_leads") {
            // Surface unreplied leads first — oldest unreplied at top so the
            // ones most at risk of slipping away show up before fresh inquiries.
            arr.sort((a, b) => {
                const aStale = a.stale_lead ? 1 : 0;
                const bStale = b.stale_lead ? 1 : 0;
                if (aStale !== bStale) return bStale - aStale;
                if (!aStale) return 0;
                const at = a.assessment_email_sent_at || "";
                const bt = b.assessment_email_sent_at || "";
                return at.localeCompare(bt);
            });
        }
        // "created" (default) — server already returns rows sorted by created_at desc
        return arr;
    }, [leads, sortBy]);

    const refreshOutreachSummary = async () => {
        try {
            const r = await axios.get(`${API}/coach/leads-outreach-summary`, { withCredentials: true });
            setOutreachSummary(r.data.summary || {});
        } catch (_e) { /* non-fatal */ }
    };

    useEffect(() => {
        if (user && (user.role === "owner" || user.role === "coach")) {
            refreshOutreachSummary();
        }
        if (user?.role === "owner") {
            axios.get(`${API}/coach/coaches`, { withCredentials: true })
                .then((r) => setCoaches((r.data || []).filter((c) => c.role === "coach" || c.role === "owner")))
                .catch(() => {});
        }
    }, [user, leads]);

    useEffect(() => {
        if (loading) return;
        if (!user) {
            navigate("/", { replace: true });
            return;
        }
        if (user.role !== "owner" && user.role !== "coach") {
            navigate("/dashboard", { replace: true });
        }
    }, [loading, user, navigate]);

    const fetchLeads = async () => {
        setLoadingLeads(true);
        try {
            const params = {};
            if (statusFilter) params.status = statusFilter;
            if (search.trim()) params.q = search.trim();
            const r = await axios.get(`${API}/coach/leads`, { params, withCredentials: true });
            setLeads(r.data.leads || []);
            setPerms(r.data.permissions || {});
        } catch (e) {
            toast.error("Couldn't load clients", {
                description: e?.response?.data?.detail || "Try again in a moment.",
            });
        } finally {
            setLoadingLeads(false);
        }
    };

    /**
     * Coach-editable Last-In override. `iso` should be an `YYYY-MM-DD` string
     * (or `null` to CLEAR the override and revert to the computed value).
     * On success, patches the local `leads` state so the row updates
     * instantly without a full refetch. Sachin 2026-02-28.
     */
    const saveLastInOverride = async (lead, iso) => {
        try {
            const r = await axios.patch(
                `${API}/coach/clients/${lead.user_id}/last-in-override`,
                { date: iso },
                { withCredentials: true },
            );
            const nextOverride = r.data?.last_attended_override_at || null;
            setLeads((prev) => prev.map((l) => {
                if (l.user_id !== lead.user_id) return l;
                // If cleared, restore the computed value the backend sent
                // us originally (stored in `last_attended_computed_at`).
                const nextTs = nextOverride || l.last_attended_computed_at || null;
                return { ...l, last_attended_at: nextTs, last_attended_override_at: nextOverride };
            }));
            toast.success(nextOverride ? "Last-In date updated" : "Override cleared");
        } catch (e) {
            toast.error("Couldn't save date", {
                description: e?.response?.data?.detail || "Try again.",
            });
            throw e;
        }
    };



    useEffect(() => {
        if (user && (user.role === "owner" || user.role === "coach")) fetchLeads();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, statusFilter]);

    // Pending assessment-request count (owner only)
    const [pendingCount, setPendingCount] = useState(0);
    useEffect(() => {
        if (user?.role !== "owner") return;
        let cancelled = false;
        axios.get(`${API}/coach/assessment-requests`, { withCredentials: true })
            .then((r) => { if (!cancelled) setPendingCount((r.data || []).length); })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [user, statusFilter]);

    // Refresh the lead list + active detail whenever a contact event is logged
    // so the "Last emailed" pill appears immediately on the right side.
    useEffect(() => {
        if (!contactTracker.lastConfirmed) return;
        const { id, type } = contactTracker.lastConfirmed;
        if (type === "lead") {
            fetchLeads();
            if (activeId === id) {
                axios.get(`${API}/coach/leads/${id}`, { withCredentials: true })
                    .then((r) => setActiveDetail(r.data))
                    .catch(() => {});
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contactTracker.lastConfirmed]);

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        fetchLeads();
    };

    const handleSelect = async (lead) => {
        setActiveId(lead.user_id);
        setActiveDetail(null);
        try {
            const r = await axios.get(`${API}/coach/leads/${lead.user_id}`, { withCredentials: true });
            setActiveDetail(r.data);
        } catch (e) {
            // Don't silently fall back to the sparse roster row — that
            // was the "black page" bug. Surface the actual error so the
            // owner knows what to fix. (2026-02-17.)
            const detail = e?.response?.data?.detail || "Couldn't load this client";
            toast.error(detail);
            setActiveId(null);
        }
    };

    // Non-destructive refetch — for actions like ensure-self-athlete that
    // shouldn't unmount the LeadDetail (would reset tab/picker state).
    const refreshActiveDetailQuiet = async () => {
        if (!activeId) return;
        try {
            const r = await axios.get(`${API}/coach/leads/${activeId}`, { withCredentials: true });
            setActiveDetail(r.data);
        } catch {
            /* keep stale data on failure */
        }
    };

    // 2026-02-23: removed StartSubscriptionButton auto-prompt (Phase 2b).

    const handleStatusChange = async (lead, status) => {
        setSavingStatus(lead.user_id + status);
        try {
            await axios.patch(
                `${API}/coach/leads/${lead.user_id}/status`,
                { status },
                { withCredentials: true },
            );
            toast.success("Status updated", { description: `${lead.name || lead.email} → ${STATUS_META[status].label}` });
            await fetchLeads();
            if (activeId === lead.user_id) {
                const r = await axios.get(`${API}/coach/leads/${lead.user_id}`, { withCredentials: true });
                setActiveDetail(r.data);
            }
            // Option D auto-prompt — only when becoming a Member from another
            // status. Phase 2b: coaches now subscribe from a pricing block
            // on the Billing tab, so we just nudge with a toast.
            if (status === "member" && lead.status !== "member") {
                toast.info("Member promoted — open Billing → Subscribe from a pricing block to start auto-billing.");
            }
        } catch (e) {
            // 2026-08: show the REAL failure reason — the old fallback
            // ("Permission may be missing") sent Sachin down the wrong path
            // when the actual cause was a network blip.
            const detail = e?.response?.data?.detail;
            const description = typeof detail === "string"
                ? detail
                : Array.isArray(detail)
                    ? detail.map((d) => d?.msg || "").filter(Boolean).join("; ") || "Invalid request"
                    : e?.response
                        ? `Server error (${e.response.status}) — try again, and tell your developer if it repeats`
                        : "Network hiccup — check your connection and try again";
            toast.error("Couldn't update status", { description });
        } finally {
            setSavingStatus(null);
        }
    };

    const counts = useMemo(() => {
        const acc = { lead: 0, member: 0, inactive: 0 };
        for (const l of leads) acc[l.status] = (acc[l.status] || 0) + 1;
        return acc;
    }, [leads]);

    if (loading || !user) {
        return (
            <div className="min-h-screen bg-brand-obsidian flex items-center justify-center">
                <Loader2 className="w-7 h-7 text-brand-red animate-spin" />
            </div>
        );
    }

    const isOwner = user.role === "owner";

    return (
        <div data-testid="coach-dashboard">
            <StripeModeBanner role={user?.role} />
            {!activeDetail && (
            <>
            <section className="relative pt-12 md:pt-10 pb-10 border-b border-zinc-900">
                <div className="absolute inset-0 bg-grid-faint opacity-30 pointer-events-none" />
                <div className="relative max-w-5xl mx-auto px-6 md:px-10">
                    <div className="text-[11px] tracking-[0.3em] uppercase text-brand-red mb-4 inline-flex items-center gap-3">
                        <span className="inline-block w-10 h-px bg-brand-red" />
                        Coach console · {isOwner ? "Admin" : "Coach"}
                    </div>
                    <h1 className="font-display text-5xl md:text-6xl tracking-wider leading-[0.92] text-white">
                        CLIENT <span className="text-brand-red">ROSTER.</span>
                    </h1>
                    <p className="mt-4 text-zinc-200 text-base max-w-2xl">
                        Every parent and athlete who&apos;s signed up. Filter by status, search by name or email,
                        tap a row to see the full file.
                    </p>

                    {isOwner && (
                        <div className="mt-5 flex flex-wrap gap-2">
                            <Link
                                to="/coach/coaches"
                                data-testid="dashboard-coaches-button"
                                className="btn-tactical inline-flex items-center gap-2 text-xs"
                            >
                                <UserCog className="w-4 h-4" /> Coaches
                            </Link>
                            <Link
                                to="/coach/find"
                                data-testid="dashboard-find-button"
                                className="btn-ghost-tactical inline-flex items-center gap-2 text-xs"
                            >
                                <Search className="w-4 h-4" /> Find anyone
                            </Link>
                            <Link
                                to="/coach/auth-health"
                                data-testid="dashboard-auth-health-button"
                                className="btn-ghost-tactical inline-flex items-center gap-2 text-xs"
                            >
                                <Activity className="w-4 h-4" /> Auth health
                            </Link>
                        </div>
                    )}

                    <div className="mt-6">
                        <BillingAlertBanner />
                        <AssessmentInProgressBanner />
                    </div>

                    {/* Daily attendance reconciliation tile (2026-06-10).
                        Compact 4-count summary + mismatch badge; tap to open
                        the full /coach/today reconciliation page. */}
                    <div className="mt-6 max-w-md">
                        <TodayAttendanceTile />
                    </div>

                    {/* Road to 200 — paying members on the standing schedule.
                        Visible to owner + coaches alike so everyone shares the
                        same KPI. (Sachin 2026-02-13) */}
                    <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-3">
                        <MemberCounter />
                        <RecurringRevenueWidget />
                    </div>

                    {/* Members without a recurring template — gap-finder
                        (Sachin 2026-02-17). Surfaces active members at risk
                        of drifting off the schedule. */}
                    <div className="mt-3">
                        <MembersWithoutRecurringTile />
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-6 gap-px bg-zinc-900 border border-zinc-900 mt-10 max-w-4xl">
                        {[
                            { k: "lead", label: "Leads", accent: "border-t-2 border-brand-red" },
                            { k: "prospect", label: "Prospects", accent: "border-t-2 border-amber-400" },
                            { k: "assessed", label: "Assessed", accent: "border-t-2 border-sky-400" },
                            { k: "member", label: "Members", accent: "border-t-2 border-brand-navy-500" },
                            { k: "non_member", label: "Non-Members", accent: "border-t-2 border-amber-500" },
                            { k: "inactive", label: "Inactive", accent: "border-t-2 border-zinc-600" },
                        ].map((s) => (
                            <div key={s.k} className={`bg-black px-5 py-4 ${s.accent}`}>
                                <div className="font-display text-3xl tracking-wider text-white">{counts[s.k] || 0}</div>
                                <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 mt-1">{s.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Demo workspace tile — owner-only, sandbox for practicing flows */}
            {isOwner && (
                <section className="border-b border-zinc-900 bg-brand-obsidian/40">
                    <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 space-y-4">
                        <UpcomingRenewalsPanel />
                        <DemoClientPanel onSeeded={fetchLeads} />
                        <ReferralSourcePanel />
                    </div>
                </section>
            )}

            <section className="py-8 border-b border-zinc-900 sticky top-16 md:top-20 bg-brand-obsidian/95 backdrop-blur z-30">
                <div className="max-w-7xl mx-auto px-6 md:px-10 space-y-3">
                    {/* Row 1 — search + Add Member + Bulk Invite. Action
                        buttons live OUTSIDE the filter scroll row so they're
                        always visible on mobile without horizontal-scrolling
                        past the filter chips. (Sachin 2026-06-08 mobile bug.) */}
                    <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
                        <form onSubmit={handleSearchSubmit} className="flex items-center gap-2 flex-1 max-w-md">
                            <div className="relative flex-1">
                                <Search className="w-4 h-4 text-zinc-300 absolute left-3 top-1/2 -translate-y-1/2" />
                                <input
                                    type="text"
                                    data-testid="coach-search"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder="Search by name, email, phone…"
                                    className="w-full bg-black border border-zinc-800 pl-10 pr-3 py-2.5 text-sm text-white placeholder:text-zinc-200 focus:outline-none focus:border-brand-red"
                                />
                            </div>
                            <button type="submit" className="btn-ghost-tactical">Search</button>
                        </form>
                        <div className="flex items-center gap-2 shrink-0">
                            {QUICK_ADD_CONFIG[statusFilter] && (
                                <button
                                    type="button"
                                    data-testid={`quick-add-${statusFilter}-btn`}
                                    onClick={() => setQuickAddOpen(statusFilter)}
                                    className="text-[10px] tracking-[0.2em] uppercase border border-brand-red bg-brand-red/10 text-brand-red hover:bg-brand-red hover:text-white px-3 py-2 transition-colors whitespace-nowrap"
                                >
                                    {QUICK_ADD_CONFIG[statusFilter].label}
                                </button>
                            )}
                            {user?.role === "owner" && (
                                <button
                                    type="button"
                                    data-testid="dashboard-bulk-invite"
                                    onClick={() => navigate("/coach/mnp")}
                                    title="Paste a list of emails to invite members in bulk"
                                    className="text-[10px] tracking-[0.2em] uppercase border border-emerald-700 bg-emerald-900/20 text-emerald-300 hover:bg-emerald-800/30 px-3 py-2 transition-colors inline-flex items-center gap-1.5 whitespace-nowrap"
                                >
                                    <Users2 className="w-3 h-3" /> Bulk invite
                                </button>
                            )}
                        </div>
                    </div>
                    {/* Row 2 — filter chips + sort. Horizontally scrollable on
                        mobile so we don't run out of space; wraps on desktop. */}
                    <div className="flex items-center gap-1.5 overflow-x-auto md:flex-wrap -mx-2 px-2 md:mx-0 md:px-0 scrollbar-thin">
                        <Filter className="w-3.5 h-3.5 text-zinc-300 mr-1 shrink-0" />
                        {FILTERS.map((f) => (
                            <button
                                key={f.key || "all"}
                                type="button"
                                data-testid={`coach-filter-${f.key || "all"}`}
                                onClick={() => setStatusFilter(f.key)}
                                className={`text-[10px] tracking-[0.2em] uppercase border px-3 py-2 transition-colors whitespace-nowrap shrink-0 ${
                                    statusFilter === f.key
                                        ? "border-brand-red text-brand-red"
                                        : "border-zinc-800 text-zinc-200 hover:text-white"
                                }`}
                            >
                                {f.label}
                            </button>
                        ))}
                        {staleCount > 0 && (
                            <button
                                type="button"
                                data-testid="coach-filter-stale-only"
                                onClick={() => setShowStaleOnly((v) => !v)}
                                className={`inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase border px-3 py-2 transition-colors whitespace-nowrap shrink-0 ${
                                    showStaleOnly
                                        ? "border-rose-500 bg-rose-950/40 text-rose-200"
                                        : "border-rose-700/70 text-rose-300 hover:bg-rose-950/30"
                                }`}
                                title="Show only leads that haven't replied within 48h of the scheduling email"
                            >
                                ⚠ {staleCount} stale
                            </button>
                        )}
                        {(() => {
                            const noSubCount = leads.filter(
                                (l) => l.status === "member" && !l.has_active_subscription
                            ).length;
                            if (noSubCount === 0) return null;
                            return (
                                <button
                                    type="button"
                                    data-testid="coach-filter-no-sub-only"
                                    onClick={() => setShowNoSubOnly((v) => !v)}
                                    className={`inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase border px-3 py-2 transition-colors whitespace-nowrap shrink-0 ${
                                        showNoSubOnly
                                            ? "border-amber-500 bg-amber-950/40 text-amber-200"
                                            : "border-amber-700/70 text-amber-300 hover:bg-amber-950/30"
                                    }`}
                                    title="Members with no recurring subscription — billing leaks"
                                >
                                    ⚠ {noSubCount} no sub
                                </button>
                            );
                        })()}
                        <select
                            data-testid="coach-roster-sort"
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                            className="text-[10px] tracking-[0.2em] uppercase border border-zinc-800 bg-black text-zinc-200 px-2.5 py-2 hover:border-brand-red hover:text-white focus:outline-none focus:border-brand-red whitespace-nowrap shrink-0"
                            aria-label="Sort roster"
                        >
                            <option value="created">Sort: Newest</option>
                            <option value="recent_attend">Sort: Recently in</option>
                            <option value="stale">Sort: Lapsed (oldest first)</option>
                            <option value="stale_leads">Sort: Stale leads (no reply)</option>
                        </select>
                        <RosterExportButtons />
                    </div>
                </div>
            </section>
            </>
            )}
            {quickAddOpen && QUICK_ADD_CONFIG[quickAddOpen] && (
                <QuickAddClientModal
                    targetStatus={QUICK_ADD_CONFIG[quickAddOpen].target_status}
                    flavor={quickAddOpen}
                    onClose={() => setQuickAddOpen(null)}
                    onCreated={() => { setQuickAddOpen(null); fetchLeads(); }}
                />
            )}

            <section className="py-8">
                <div className="max-w-7xl mx-auto px-6 md:px-10">
                    {activeDetail ? (
                        <div data-testid="coach-lead-detail-fullwidth">
                            <button
                                type="button"
                                data-testid="coach-back-to-roster"
                                onClick={() => { setActiveDetail(null); setActiveId(null); }}
                                className="inline-flex items-center gap-2 text-sm text-zinc-300 hover:text-white mb-5 px-2 py-1 -ml-2"
                            >
                                <ArrowLeft className="w-4 h-4" /> Back to roster
                            </button>
                            <LeadDetail
                                lead={activeDetail}
                                perms={perms}
                                onChangeStatus={(s) => handleStatusChange(activeDetail, s)}
                                saving={savingStatus}
                                contactTracker={contactTracker}
                                onOutreachLogged={refreshOutreachSummary}
                                isOwner={user?.role === "owner"}
                                coaches={coaches}
                                onMetaChanged={() => activeDetail && handleSelect(activeDetail)}
                                onAthletesChanged={refreshActiveDetailQuiet}
                                onDeleted={async () => {
                                    setActiveDetail(null);
                                    setActiveId(null);
                                    await fetchLeads();
                                }}
                            />
                        </div>
                    ) : (
                        <div data-testid="coach-leads-list">
                            {loadingLeads ? (
                                <div className="py-20 flex items-center justify-center">
                                    <Loader2 className="w-6 h-6 text-brand-red animate-spin" />
                                </div>
                            ) : (() => {
                                const rows = (() => {
                                    let arr = sortedLeads;
                                    if (showStaleOnly) arr = arr.filter((l) => l.stale_lead);
                                    if (showNoSubOnly) arr = arr.filter((l) => l.status === "member" && !l.has_active_subscription);
                                    return arr;
                                })();
                                if (rows.length === 0) {
                                    return (
                                        <div className="text-center py-16 border border-zinc-900 bg-zinc-950">
                                            <Users className="w-8 h-8 text-zinc-200 mx-auto mb-3" />
                                            <div className="font-display text-2xl tracking-wider text-white">
                                                {showStaleOnly ? "No stale leads — great job!" : "No clients yet"}
                                            </div>
                                            <div className="text-sm text-zinc-300 mt-2">
                                                {showStaleOnly
                                                    ? "Every lead has either replied or been scheduled within 48h."
                                                    : (search ? "Try clearing the search." : "New signups will appear here.")}
                                            </div>
                                        </div>
                                    );
                                }
                                return (
                                <div className="border border-zinc-900">
                                    {rows.map((l) => {
                                        const stop = (e) => e.stopPropagation();
                                        const athletes = Array.isArray(l.athletes) ? l.athletes : [];
                                        return (
                                            <div key={l.user_id}>
                                            <div
                                                role="button"
                                                tabIndex={0}
                                                data-testid={`coach-lead-row-${l.user_id}`}
                                                onClick={() => handleSelect(l)}
                                                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleSelect(l); } }}
                                                className={`group w-full cursor-pointer text-left px-4 sm:px-5 py-3 flex items-center gap-3 sm:gap-4 border-b border-zinc-900 transition-colors focus:outline-none focus:bg-zinc-900/60 ${
                                                    activeId === l.user_id ? "bg-brand-red/5" : "bg-zinc-950 hover:bg-zinc-900/50"
                                                }`}
                                            >
                                                <span className="inline-flex items-center justify-center w-10 h-10 bg-brand-red text-white font-display text-base flex-shrink-0">
                                                    {(l.parent_first_name?.[0] || l.name?.[0] || l.email[0] || "A").toUpperCase()}
                                                </span>

                                                {/* Name only — Sachin spec 2026-02-18: collapse rows to just the name
                                                    for fast scanning. All status/billing/outreach detail lives
                                                    inside the dossier; search still spans parent + athletes. */}
                                                <div className="min-w-0 flex-1 flex items-center gap-2 flex-wrap">
                                                    <span className="text-base sm:text-sm font-semibold text-white truncate">
                                                        {l.parent_first_name ? `${l.parent_first_name} ${l.parent_last_name || ""}`.trim() : l.name || l.email}
                                                    </span>
                                                    {/* Editable "Last in" date. Sachin 2026-02-28:
                                                        "make it so dates people are logged editable
                                                        and make sure it stays there." Overrides go
                                                        to `last_attended_override_at` on the user
                                                        doc so the ledger stays intact and the value
                                                        is durable across reloads. */}
                                                    <span onClick={stop} onKeyDown={stop}>
                                                        <LastInBadge
                                                            ts={l.last_attended_at}
                                                            lead={l}
                                                            onOverride={(iso) => saveLastInOverride(l, iso)}
                                                        />
                                                    </span>
                                                </div>

                                                {/* Quick action: send pricing — hover-reveal $ icon. Stops
                                                    propagation so it doesn't open the dossier. Sachin spec
                                                    2026-02-28: batch-blast prices from the roster. */}
                                                <SendPricingButton
                                                    compact
                                                    leadUserId={l.user_id}
                                                    leadEmail={l.email}
                                                    onSent={refreshOutreachSummary}
                                                />

                                                <ChevronRight className="w-4 h-4 text-zinc-400 flex-shrink-0 self-center" />
                                            </div>
                                            {/* Indented athlete rows — searchable, clickable straight into the dossier */}
                                            {athletes.map((a) => (
                                                <Link
                                                    key={a.athlete_id}
                                                    to={`/coach/athletes/${a.athlete_id}`}
                                                    data-testid={`coach-roster-athlete-${a.athlete_id}`}
                                                    onClick={stop}
                                                    className="flex items-center gap-3 pl-12 sm:pl-16 pr-4 py-2 border-b border-zinc-900 last:border-b-0 bg-zinc-950/40 hover:bg-zinc-900/60 transition-colors group"
                                                >
                                                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-zinc-800 group-hover:bg-brand-red/30 text-zinc-300 group-hover:text-white text-[10px] font-semibold transition-colors">
                                                        {(a.first_name?.[0] || "A").toUpperCase()}
                                                    </span>
                                                    <span className="text-sm text-zinc-200 group-hover:text-white truncate">
                                                        {a.first_name} {a.last_name || ""}
                                                    </span>
                                                    <ChevronRight className="w-3 h-3 text-zinc-600 group-hover:text-brand-red ml-auto" />
                                                </Link>
                                            ))}
                                            </div>
                                        );
                                    })}
                                </div>
                                );
                            })()}
                        </div>
                    )}
                </div>
            </section>
            <ContactConfirmBanner tracker={contactTracker} />
        </div>
    );
}


/**
 * WaiverPill — compact roster-row indicator for an athlete's waiver status.
 * Sachin spec 2026-02-28: scan a 200-row roster in 5 seconds and spot
 * which families need a re-sign without opening any dossier. Hover/title
 * surfaces the exact state.
 */
function WaiverPill({ status }) {
    if (!status) return null;
    const cfg = {
        current:  { className: "border-emerald-600/40 text-emerald-300", label: "Waiver", title: "Waiver signed and current" },
        expiring: { className: "border-amber-600/50 text-amber-300",   label: "Waiver · expiring", title: "Waiver expires in less than 30 days" },
        expired:  { className: "border-red-700/60 bg-red-900/15 text-red-300", label: "Waiver · expired", title: "Waiver has expired or version is outdated — parent must re-sign" },
        missing:  { className: "border-red-700/60 bg-red-900/15 text-red-300", label: "No waiver", title: "No waiver on file — parent must sign on next login" },
    }[status] || null;
    if (!cfg) return null;
    return (
        <span
            data-testid={`athlete-waiver-pill-${status}`}
            title={cfg.title}
            className={`inline-flex items-center gap-1 text-[9px] tracking-[0.2em] uppercase border px-1.5 py-0.5 ${cfg.className}`}
        >
            <ShieldCheck className="w-3 h-3" />
            {cfg.label}
        </span>
    );
}


/**
 * AthleteLeadRow
 * --------------
 * Single athlete row in the Lead Detail Athletes section.
 * Click-to-expand reveals (a) the kid's own subscription line(s) and
 * (b) an inline Subscribe widget pinned to that athlete.
 * Yellow "Needs assignment" chip surfaces account-wide subs that
 * predate the Spotify-Plan model (no athlete_id).
 */
function AthleteLeadRow({ athlete: a, lead, perms, onEditAthlete, onSubscribed }) {
    const [expanded, setExpanded] = useState(false);
    const age = calcAge(a.dob);
    const allSubs = lead.active_subscriptions || [];
    const ownSubs = allSubs.filter((s) => s.athlete_id === a.athlete_id);
    const unassignedSubs = allSubs.filter((s) => s.legacy_unassigned);
    const hasOwnSub = ownSubs.length > 0;
    const planLabel = (s) => s.plan_name || (s.status === "trialing" ? "Trial" : "Subscription");
    const moneyShort = (cents, interval) => {
        if (cents == null) return "";
        const dollars = Math.round((cents || 0) / 100);
        const i = ({ daily: "day", weekly: "wk", monthly: "mo" }[interval] || interval || "");
        return `$${dollars}/${i}`;
    };
    return (
        <div data-testid={`lead-detail-athlete-row-${a.athlete_id}`} className="relative border border-zinc-900 bg-black/40">
            <div className="p-3.5">
                <button
                    type="button"
                    data-testid={`lead-detail-edit-athlete-${a.athlete_id}`}
                    onClick={onEditAthlete}
                    className="absolute top-2.5 right-2.5 inline-flex items-center gap-1 text-[10px] tracking-[0.2em] uppercase text-zinc-400 hover:text-brand-red transition-colors"
                    aria-label="Edit athlete"
                >
                    <Pencil className="w-3 h-3" /> Edit
                </button>
                <div className="font-display text-xl tracking-wider text-white pr-14 flex items-center gap-2 flex-wrap">
                    <AthleteNameLink
                        athleteId={a.athlete_id}
                        name={`${a.first_name || ""} ${a.last_name || ""}`.trim()}
                        testid={`lead-detail-athlete-name-${a.athlete_id}`}
                        className="font-display text-xl tracking-wider text-white hover:text-brand-red underline-offset-2 hover:underline transition-colors"
                    />
                    <CoverageDot coverage={a.coverage} athleteId={a.athlete_id} />
                    <WaiverPill status={a.waiver_status} />
                </div>
                <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-300 mt-1">
                    {age != null ? `Age ${age}` : "—"} · {TRACK_LABELS[a.track] || a.track}
                </div>
                <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                    {hasOwnSub ? (
                        ownSubs.map((s) => {
                            const d = s.discount;
                            const dLabel = d?.kind === "percent"
                                ? `${d.pct}% off ${d.duration === "forever" ? "forever" : d.duration === "repeating" ? `${d.periods}p` : "once"}`
                                : d?.kind === "fixed"
                                    ? `$${((d.cents_per_period || 0) / 100).toFixed(0)} off ${d.duration === "forever" ? "forever" : d.duration === "repeating" ? `${d.periods}p` : "once"}`
                                    : null;
                            return (
                                <span
                                    key={s.subscription_id}
                                    data-testid={`athlete-sub-chip-${a.athlete_id}`}
                                    className="inline-flex items-center gap-1 text-[9px] tracking-[0.2em] uppercase border border-emerald-500/40 text-emerald-300 px-2 py-0.5"
                                    title={dLabel ? `Discount applied: ${dLabel}${d.reason ? ` — ${d.reason}` : ""}` : undefined}
                                >
                                    {planLabel(s)} {s.price_cents || s.amount_cents ? `· ${moneyShort(s.price_cents || s.amount_cents, s.interval)}` : ""}
                                    {dLabel && (
                                        <span className="ml-1 px-1 border border-amber-500/60 bg-amber-900/30 text-amber-200 text-[8px] tracking-[0.15em]" data-testid={`athlete-sub-discount-${a.athlete_id}`}>
                                            {dLabel}
                                        </span>
                                    )}
                                </span>
                            );
                        })
                    ) : (
                        <span data-testid={`athlete-sub-chip-${a.athlete_id}`} className="inline-flex items-center gap-1 text-[9px] tracking-[0.2em] uppercase border border-zinc-700 text-zinc-400 px-2 py-0.5">
                            No subscription
                        </span>
                    )}
                </div>
                {perms.view_health_notes && a.health_notes && (
                    <div className="mt-2.5 flex items-start gap-1.5 text-xs text-zinc-200">
                        <Heart className="w-3 h-3 text-brand-red mt-0.5 flex-shrink-0" />
                        <span>{a.health_notes}</span>
                    </div>
                )}
                <div className="mt-3 flex items-center gap-3 flex-wrap">
                    <Link
                        to={`/coach/athletes/${a.athlete_id}`}
                        data-testid={`open-dossier-${a.athlete_id}`}
                        className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase text-brand-red hover:text-white"
                    >
                        <ClipboardList className="w-3 h-3" /> Open dossier
                    </Link>
                    <button
                        type="button"
                        data-testid={`athlete-row-toggle-${a.athlete_id}`}
                        onClick={() => setExpanded((v) => !v)}
                        className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase text-zinc-400 hover:text-brand-red transition-colors"
                    >
                        {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                        {hasOwnSub ? "Change plan" : "Subscribe"}
                    </button>
                </div>
            </div>
            {expanded && (
                <div
                    data-testid={`athlete-row-expanded-${a.athlete_id}`}
                    className="border-t border-zinc-900 p-3.5 bg-zinc-950/60 space-y-3"
                >
                    {unassignedSubs.length > 0 && (
                        <div className="border border-amber-700/40 bg-amber-900/15 px-3 py-2 text-[11px] text-amber-200 leading-snug">
                            <strong className="tracking-[0.15em] uppercase text-amber-300">Needs assignment:</strong>{" "}
                            {unassignedSubs.length} legacy account-wide sub{unassignedSubs.length === 1 ? "" : "s"} on this account.{" "}
                            Reassign from the <strong>Billing</strong> tab so each kid&apos;s access is tracked separately.
                        </div>
                    )}
                    <AthleteSubscribeWidget
                        parentUserId={lead.user_id}
                        athleteId={a.athlete_id}
                        athleteFirstName={a.first_name}
                        athleteLastName={a.last_name}
                        onSubscribed={() => { setExpanded(false); onSubscribed?.(); }}
                    />
                </div>
            )}
        </div>
    );
}

function LeadDetail({ lead, perms, onChangeStatus, saving, contactTracker, onOutreachLogged, isOwner, coaches, onMetaChanged, onAthletesChanged, onDeleted }) {
    const navigate = useNavigate();
    const [chargeOpen, setChargeOpen] = useState(false);
    const [enrollOpen, setEnrollOpen] = useState(false);
    const [editAccountOpen, setEditAccountOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    // Tab consolidation (2026-06-08): collapsed the long-scroll layout into
    // 3 tabs — Info · Billing · Schedule. Each tab is one tap from the top
    // of the profile so coaches don't have to scroll-hunt for "Charge a
    // pack" / "Schedule a session" / "Outreach tracker".
    const [tab, setTab] = useState("info");
    const [scheduleAthleteId, setScheduleAthleteId] = useState(null);

    // Bridge: CurrentBillingPanel fires this event after a backdated session
    // log so the lead detail re-fetches itself (refreshes pack count + active
    // packs panel).
    useEffect(() => {
        const handler = () => onMetaChanged?.();
        window.addEventListener("maxsc:lead-meta-refresh", handler);
        return () => window.removeEventListener("maxsc:lead-meta-refresh", handler);
    }, [onMetaChanged]);
    const composeUrl = useGmailComposeUrl();
    const [assessmentAthlete, setAssessmentAthlete] = useState(null);
    const [rescheduleOpen, setRescheduleOpen] = useState(false);
    const [scheduleAssessOpen, setScheduleAssessOpen] = useState(false);
    const [athleteEditor, setAthleteEditor] = useState(null); // null | {mode, athlete?}
    const status = STATUS_META[lead.status] || STATUS_META.lead;
    const addr = lead.address || {};
    const addrLine =
        [addr.line1, [addr.city, addr.state].filter(Boolean).join(", "), addr.postal_code]
            .filter(Boolean)
            .join(" · ") || "—";
    const fullName = lead.parent_first_name
        ? `${lead.parent_first_name} ${lead.parent_last_name || ""}`
        : lead.name || lead.email;

    const handleFunnelClick = (stageKey, { done }) => {
        // Scheduled stage with a confirmed slot: open the reschedule picker
        // (done or not) instead of a dead-end toast.
        if (stageKey === "scheduled" && lead.next_assessment) {
            setRescheduleOpen(true);
            return;
        }
        // "done" stages: do nothing — read-only confirmation that it's complete.
        if (done) {
            toast.success(`${stageKey} — already done.`);
            return;
        }
        if (stageKey === "contacted") {
            // Scroll to the outreach tracker
            const el = document.querySelector('[data-testid="lead-outreach-tracker"]');
            if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
            else toast.info("Log a call, text, or email below to mark Contacted.");
            return;
        }
        if (stageKey === "scheduled") {
            // Schedule an assessment for THIS profile directly (works for
            // prospects too) — no dead-end trip to the Pending inbox.
            setScheduleAssessOpen(true);
            return;
        }
        if (stageKey === "assessed") {
            // Open the assessment form for the first athlete (or only one).
            const first = (lead.athletes || [])[0];
            if (!first) {
                toast.error("No athlete on this lead — add one before running an assessment.");
                return;
            }
            setAssessmentAthlete(first);
            return;
        }
        if (stageKey === "signed") {
            if (!isOwner) {
                toast.info("Only admins can send enrollment links.");
                return;
            }
            setEnrollOpen(true);
        }
    };

    return (
        <div data-testid="coach-lead-detail" className="border border-zinc-900 bg-zinc-950 max-w-4xl mx-auto">
            <div className="p-6 border-b border-zinc-900">
                <div className="flex items-center justify-between gap-3">
                    <span className={`inline-flex items-center gap-2 border px-3 py-1.5 text-[10px] tracking-[0.25em] uppercase ${status.className}`}>
                        <BadgeCheck className="w-3.5 h-3.5" />
                        {status.label}
                    </span>
                    <span className="text-[10px] tracking-[0.2em] uppercase text-zinc-300">
                        {lead.signup_for === "self" ? "Adult" : lead.signup_for === "both" ? "Family + Self" : "Family"}
                    </span>
                </div>
                <div className="font-display text-3xl tracking-wider text-white mt-3 leading-tight flex items-start gap-2 flex-wrap">
                    <span className="break-words">{fullName}</span>
                    {isOwner && (
                        <button
                            type="button"
                            data-testid="lead-detail-edit-name"
                            onClick={() => setEditAccountOpen(true)}
                            title="Edit profile — name, email, phone, etc."
                            className="self-center text-zinc-400 hover:text-brand-red transition-colors p-1.5 border border-zinc-800 hover:border-brand-red rounded"
                        >
                            <Pencil className="w-4 h-4" />
                        </button>
                    )}
                    <button
                        type="button"
                        data-testid="lead-detail-add-athlete"
                        onClick={() => setAthleteEditor({ mode: "add" })}
                        title="Add a kid / athlete to this client's profile"
                        className="self-center inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase border border-brand-red bg-brand-red/10 text-brand-red hover:bg-brand-red hover:text-white transition-colors px-2.5 py-1.5 rounded ml-1"
                    >
                        <Plus className="w-3.5 h-3.5" /> Add athlete
                    </button>
                    <EnterCardHeaderButton
                        userId={lead.user_id}
                        onFallback={() => {
                            setTab("billing");
                            setTimeout(() => {
                                document.querySelector('[data-testid="billing-card-on-file-section"]')
                                    ?.scrollIntoView({ behavior: "smooth", block: "center" });
                            }, 300);
                        }}
                    />
                </div>
                <p className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 mt-1 flex items-center gap-2 flex-wrap">
                    Primary account
                    <CardOnFileChip
                        userId={lead.user_id}
                        initialCached={!!lead.card_on_file}
                        onAddCard={() => {
                            setTab("billing");
                            setTimeout(() => {
                                document.querySelector('[data-testid="billing-card-on-file-section"]')
                                    ?.scrollIntoView({ behavior: "smooth", block: "center" });
                            }, 300);
                        }}
                    />
                    <SubscriptionStatusChip userId={lead.user_id} />
                </p>
                <ul className="mt-4 space-y-1.5 text-sm">
                    <li className="flex items-center gap-2 text-zinc-300 flex-wrap">
                        <Mail className="w-4 h-4 text-brand-red" />
                        <a href={composeUrl.intro(lead).url} target="_blank" rel="noopener noreferrer" title={composeUrl.title} className="hover:text-brand-red transition-colors">{lead.email}</a>
                        <SendPricingButton
                            leadUserId={lead.user_id}
                            leadEmail={lead.email}
                            onSent={() => { onMetaChanged?.(); onOutreachLogged?.(); }}
                        />
                        {/* Sachin 2026-03-05: one-tap in-app message.
                            Opens/reuses a thread with this account and
                            navigates to /coach/messages?t=<id> so the
                            reply lands in the same inbox. */}
                        <button
                            type="button"
                            data-testid="lead-message-btn"
                            onClick={async () => {
                                try {
                                    const r = await axios.post(
                                        `${process.env.REACT_APP_BACKEND_URL}/api/messages/start-thread`,
                                        { recipient_user_id: lead.user_id, body: "" },
                                        { withCredentials: true },
                                    );
                                    const tid = r.data?.thread_id;
                                    if (!tid) throw new Error("No thread returned");
                                    navigate(`/coach/messages?t=${encodeURIComponent(tid)}`);
                                } catch (e) {
                                    toast.error("Couldn't open the thread", {
                                        description: e?.response?.data?.detail || e?.message,
                                    });
                                }
                            }}
                            className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] tracking-[0.15em] uppercase border border-zinc-800 hover:border-brand-red hover:text-brand-red text-zinc-300 transition-colors"
                            title="Send an in-app message — saved in Messages"
                        >
                            <MessageSquare className="w-3 h-3" /> Message
                        </button>
                    </li>
                    {perms.view_address_phone && lead.phone && (
                        <li className="flex items-center gap-2 text-zinc-300">
                            <Phone className="w-4 h-4 text-brand-red" />
                            <a href={`tel:${lead.phone}`} className="hover:text-brand-red transition-colors">{lead.phone}</a>
                        </li>
                    )}
                    {perms.view_address_phone && (
                        <li className="flex items-start gap-2 text-zinc-200">
                            <MapPin className="w-4 h-4 text-brand-red mt-0.5" /> {addrLine}
                        </li>
                    )}
                </ul>
            </div>

            {/* ===== SUB-PROFILES (athletes) — pinned directly under the
                 account name, always visible, for quick access. Sachin 2026-06 ===== */}
            {perms.view_athletes && lead.athletes?.length > 0 && (
                <div className="px-6 py-4 border-b border-zinc-900 bg-black/30" data-testid="lead-detail-athletes-pinned">
                    <div className="flex items-center justify-between mb-3">
                        <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-300">Athletes on this account</div>
                        <button
                            type="button"
                            data-testid="lead-detail-add-athlete-inline"
                            onClick={() => setAthleteEditor({ mode: "add" })}
                            className="inline-flex items-center gap-1 text-[10px] tracking-[0.2em] uppercase text-brand-red hover:text-white transition-colors"
                        >
                            <Plus className="w-3 h-3" /> Add athlete
                        </button>
                    </div>
                    <div className="space-y-3">
                        {lead.athletes.map((a) => (
                            <AthleteLeadRow
                                key={a.athlete_id}
                                athlete={a}
                                lead={lead}
                                perms={perms}
                                onEditAthlete={() => setAthleteEditor({ mode: "edit", athlete: a })}
                                onSubscribed={onMetaChanged}
                            />
                        ))}
                    </div>
                </div>
            )}
            {perms.view_athletes && (!lead.athletes || lead.athletes.length === 0) && (
                <div className="px-6 py-4 border-b border-zinc-900 bg-black/30" data-testid="lead-detail-athletes-pinned-empty">
                    <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 mb-3">Athletes on this account</div>
                    <div className="border border-dashed border-zinc-800 rounded-lg px-4 py-5 text-center">
                        <p className="text-sm text-zinc-300 mb-3">
                            No athletes on this profile yet.
                        </p>
                        <button
                            type="button"
                            data-testid="lead-detail-add-first-athlete"
                            onClick={() => setAthleteEditor({ mode: "add" })}
                            className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase border border-brand-red bg-brand-red/10 text-brand-red hover:bg-brand-red hover:text-white transition-colors px-3 py-2 rounded"
                        >
                            <Plus className="w-3 h-3" /> Add first athlete
                        </button>
                    </div>
                </div>
            )}

            {/* ===== TAB STRIP (Info · Billing · Schedule) ===== */}
            <div className="px-6 pt-4 border-b border-zinc-900 sticky top-0 z-10 bg-zinc-950">
                <div data-testid="lead-detail-tabs" className="flex gap-1 overflow-x-auto -mb-px">
                    {[
                        { key: "info", label: "Info", icon: Users },
                        ...(isOwner || perms.view_billing ? [{ key: "billing", label: "Billing", icon: CreditCard }] : []),
                        ...(isOwner || perms.view_schedule ? [{ key: "schedule", label: "Schedule", icon: CalendarDays }] : []),
                    ].map((t) => {
                        const Icon = t.icon;
                        const on = tab === t.key;
                        return (
                            <button
                                key={t.key}
                                type="button"
                                data-testid={`lead-tab-${t.key}`}
                                onClick={() => setTab(t.key)}
                                className={`inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-3 text-[11px] tracking-wider sm:tracking-[0.2em] uppercase border-b-2 transition-colors whitespace-nowrap flex-shrink-0 ${
                                    on
                                        ? "border-brand-red text-white"
                                        : "border-transparent text-zinc-300 hover:text-white"
                                }`}
                            >
                                <Icon className="w-3.5 h-3.5" />
                                {t.label}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* ===== INFO TAB ===== */}
            {tab === "info" && (
            <>
            {lead.funnel && (
                <SectionErrorBoundary section="Funnel progress">
                    <div className="p-6 border-b border-zinc-900">
                        <div className="flex items-center justify-between mb-3">
                            <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-400">Funnel progress</div>
                            <div className="text-[9px] tracking-wider uppercase text-zinc-500">Tap a stage to act</div>
                        </div>
                        <LeadFunnelBar funnel={lead.funnel} onStageClick={handleFunnelClick} />
                        {lead.next_assessment?.slot?.date && (
                            <div
                                data-testid="lead-next-assessment"
                                className="mt-4 border border-emerald-700/60 bg-emerald-950/30 rounded-lg p-3.5 flex flex-wrap items-center justify-between gap-3"
                            >
                                <div className="min-w-0">
                                    <div className="text-[10px] tracking-[0.25em] uppercase text-emerald-300 mb-0.5">Assessment scheduled</div>
                                    <div className="font-display text-lg tracking-wider text-white">
                                        {(() => {
                                            // Defensive: malformed date strings used to throw and blank the page.
                                            try {
                                                const d = new Date(`${lead.next_assessment.slot.date}T00:00:00`);
                                                if (Number.isNaN(d.getTime())) return lead.next_assessment.slot.date;
                                                return d.toLocaleDateString(undefined, {
                                                    weekday: "long", month: "long", day: "numeric",
                                                });
                                            } catch { return lead.next_assessment.slot.date; }
                                        })()}
                                        {lead.next_assessment.slot.start_time && (
                                            <>
                                                {" · "}
                                                {formatTimeDisplay(lead.next_assessment.slot.start_time)}
                                            </>
                                        )}
                                    </div>
                                {lead.next_assessment.athlete_name && (
                                    <div className="text-[11px] text-zinc-400 mt-0.5">
                                        <AthleteNameLink
                                            athleteId={lead.next_assessment.athlete_id}
                                            name={lead.next_assessment.athlete_name}
                                            testid={`next-assessment-athlete-${lead.next_assessment.athlete_id || "none"}`}
                                            className="text-[11px] text-zinc-400 hover:text-brand-red underline-offset-2 hover:underline transition-colors"
                                        />
                                    </div>
                                )}
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                                <button
                                    type="button"
                                    data-testid="lead-reschedule-assessment"
                                    onClick={() => setRescheduleOpen(true)}
                                    className="btn-ghost-tactical text-xs whitespace-nowrap"
                                >
                                    <CalendarDays className="w-3.5 h-3.5" /> Reschedule
                                </button>
                                <button
                                    type="button"
                                    data-testid="lead-cancel-assessment"
                                    onClick={async () => {
                                        if (!window.confirm("Cancel this scheduled assessment? The slot will be freed and they'll go back to needing a time.")) return;
                                        try {
                                            await axios.post(
                                                `${API}/coach/assessment-requests/${lead.next_assessment.request_id}/unschedule`,
                                                {}, { withCredentials: true },
                                            );
                                            toast.success("Assessment cancelled");
                                            onMetaChanged?.();
                                        } catch (e) {
                                            toast.error("Cancel failed", { description: e?.response?.data?.detail });
                                        }
                                    }}
                                    className="text-xs text-zinc-400 hover:text-brand-red whitespace-nowrap"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    )}
                </div>
                </SectionErrorBoundary>
            )}
            {lead.last_contacted_at && (
                <div
                    data-testid="lead-last-contacted"
                    className={`mx-6 mt-4 flex items-center gap-2 text-[11px] tracking-wider px-3 py-2 border ${
                        isContactStale(lead.last_contacted_at, 7)
                            ? "border-amber-700/60 bg-amber-950/30 text-amber-200"
                            : "border-emerald-900/60 bg-emerald-950/20 text-emerald-200"
                    }`}
                >
                    <Mail className="w-3.5 h-3.5" />
                    <span>
                        Last emailed by <strong>{lead.last_contacted_by_name || "—"}</strong> · {lastContactedLabel(lead.last_contacted_at)}
                    </span>
                    {isContactStale(lead.last_contacted_at, 7) && (
                        <span className="ml-auto text-[10px] uppercase tracking-[0.2em] text-amber-300">Follow up</span>
                    )}
                </div>
            )}
            </>
            )}

            {/* ===== INFO content (continued, per-section conditionals) ===== */}

            {/* Outreach tracker — available for every client (lead, member, non-member) */}
            {tab === "info" && (lead.status === "lead" || lead.role === "client") && (
                <SectionErrorBoundary section="Outreach tracker">
                    <LeadOutreachTracker leadUserId={lead.user_id} onLogged={() => onOutreachLogged?.()} />
                </SectionErrorBoundary>
            )}

            {/* Reply status — stale-lead red alert + "Mark replied" toggle.
                Only renders for leads (members/non-members already in flow). */}
            {tab === "info" && lead.status === "lead" && (
                <SectionErrorBoundary section="Reply status">
                    <LeadReplyControls lead={lead} onChanged={onMetaChanged} />
                </SectionErrorBoundary>
            )}

            {/* Original intake submission(s) — what they filled out when they booked */}
            {tab === "info" && (
                <SectionErrorBoundary section="Intake submissions">
                    <IntakeSubmissionPanel submissions={lead.intake_submissions || []} />
                </SectionErrorBoundary>
            )}

            {/* Unified Timeline — every interaction across emails, messages,
                bookings, billing, audit. Single chronological view so
                nothing gets lost. (Sachin 2026-02-13) */}
            {tab === "info" && (
                <SectionErrorBoundary section="Timeline">
                    <div className="mt-4">
                        <UnifiedTimeline userId={lead.user_id} />
                    </div>
                </SectionErrorBoundary>
            )}

            {/* Communication log — coach-side compose + email send detail */}
            {tab === "info" && (
                <SectionErrorBoundary section="Communication log">
                    <CommunicationLogPanel userId={lead.user_id} leadEmail={lead.email} />
                </SectionErrorBoundary>
            )}

            {/* Waiver — signed status + signature details */}
            {tab === "info" && (
                <SectionErrorBoundary section="Waiver">
                    <WaiverPanel waiver={lead.waiver} userEmail={lead.email} userId={lead.user_id} />
                </SectionErrorBoundary>
            )}

            {/* Profile metadata + assigned coach */}
            {tab === "info" && (
                <SectionErrorBoundary section="Profile metadata">
                    <LeadProfileMetaEditor lead={lead} isOwner={isOwner} coaches={coaches} onChanged={onMetaChanged} />
                </SectionErrorBoundary>
            )}

            {/* ===== BILLING TAB ===== */}
            {tab === "billing" && (
                <SectionErrorBoundary section="Billing">
                    <LeadDetailBilling
                        lead={lead}
                        isOwner={isOwner}
                        onMetaChanged={onMetaChanged}
                        onOpenEnroll={() => setEnrollOpen(true)}
                        onOpenCharge={() => setChargeOpen?.(true)}
                    />
                </SectionErrorBoundary>
            )}

            {/* ===== SCHEDULE TAB ===== */}
            {tab === "schedule" && (
                <SectionErrorBoundary section="Schedule">
                    <LeadDetailSchedule
                        lead={lead}
                        scheduleAthleteId={scheduleAthleteId}
                        setScheduleAthleteId={setScheduleAthleteId}
                        onAddAthlete={() => setAthleteEditor({ mode: "add" })}
                        onAthletesChanged={onAthletesChanged}
                    />
                </SectionErrorBoundary>
            )}

            {/* ===== ORIGINAL SECTIONS — kept for Info tab, conditioned below ===== */}

            {/* Athletes / sub-profiles are now pinned directly under the
                account name (above the tab strip) for quick access. */}


            {tab === "info" && perms.view_emergency_contact && lead.emergency_contact && (
                <div className="p-6 border-b border-zinc-900">
                    <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 mb-2">Emergency contact</div>
                    <div className="text-sm text-white">
                        {lead.emergency_contact.name}{" "}
                        <span className="text-zinc-300">· {lead.emergency_contact.relationship}</span>
                    </div>
                    <a href={`tel:${lead.emergency_contact.phone}`} className="text-sm text-zinc-200 hover:text-brand-red">
                        {lead.emergency_contact.phone}
                    </a>
                </div>
            )}

            {tab === "info" && perms.view_waiver && lead.waiver && (
                <div className="p-6 border-b border-zinc-900 flex items-start gap-3">
                    <FileSignature className="w-4 h-4 text-brand-red flex-shrink-0 mt-0.5" />
                    <div className="text-xs text-zinc-200">
                        Waiver {lead.waiver.version} signed by{" "}
                        <span className="text-white">{lead.waiver.signed_name}</span>
                        {lead.waiver.signed_at && (
                            <> on {new Date(lead.waiver.signed_at).toLocaleDateString()}</>
                        )}.
                    </div>
                </div>
            )}

            {tab === "info" && (perms.change_status ? (
                <div className="p-6 space-y-2">
                    <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 mb-1">Change status</div>
                    <div className="grid grid-cols-2 gap-2">
                        {[
                            { v: "lead", icon: Users, label: "Lead" },
                            { v: "prospect", icon: CalendarDays, label: "Prospect" },
                            { v: "assessed", icon: ClipboardList, label: "Assessed" },
                            { v: "member", icon: UserCheck, label: "Member" },
                            { v: "non_member", icon: UserX, label: "Non-Member" },
                            { v: "inactive", icon: UserMinus, label: "Inactive" },
                        ].map((opt) => {
                            const isActive = lead.status === opt.v;
                            const isSaving = saving === lead.user_id + opt.v;
                            return (
                                <button
                                    key={opt.v}
                                    type="button"
                                    data-testid={`coach-status-${opt.v}`}
                                    onClick={() => onChangeStatus(opt.v)}
                                    disabled={isActive || !!saving}
                                    className={`inline-flex flex-col items-center gap-1.5 py-3 border text-[10px] tracking-[0.2em] uppercase transition-colors disabled:cursor-not-allowed ${
                                        isActive
                                            ? "border-brand-red text-brand-red bg-brand-red/5"
                                            : "border-zinc-800 text-zinc-300 hover:border-brand-red hover:text-brand-red"
                                    }`}
                                >
                                    {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <opt.icon className="w-4 h-4" />}
                                    {opt.label}
                                </button>
                            );
                        })}
                    </div>
                </div>
            ) : (
                <div className="p-6 text-[11px] text-zinc-200">
                    Status changes are not enabled for your role.
                </div>
            ))}

            {tab === "info" && isOwner && (
                <div className="p-6 border-t border-zinc-900">
                    <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-400 mb-2">Danger zone</div>
                    <button
                        type="button"
                        data-testid="coach-delete-lead"
                        disabled={deleting}
                        onClick={async () => {
                            const name = lead.parent_first_name
                                ? `${lead.parent_first_name} ${lead.parent_last_name || ""}`.trim()
                                : lead.name || lead.email;
                            const confirm1 = window.confirm(
                                `Permanently delete ${name} (${lead.email})?\n\nThis erases their profile, athletes, bookings, packs, subscriptions, messages, and chat history. There is no undo.`
                            );
                            if (!confirm1) return;
                            const confirm2 = window.prompt(
                                `Type DELETE in caps to confirm.`
                            );
                            if (confirm2 !== "DELETE") {
                                toast.info("Cancelled — confirmation didn't match.");
                                return;
                            }
                            setDeleting(true);
                            try {
                                const r = await axios.delete(`${API}/coach/leads/${lead.user_id}`, { withCredentials: true });
                                const counts = r.data?.deleted_counts || {};
                                toast.success(`Deleted ${name}`, {
                                    description: `${Object.values(counts).reduce((a, b) => a + b, 0)} rows wiped across ${Object.keys(counts).length} collections.`,
                                });
                                await onDeleted?.();
                            } catch (e) {
                                toast.error("Couldn't delete", { description: e?.response?.data?.detail || "Try again." });
                            } finally {
                                setDeleting(false);
                            }
                        }}
                        className="inline-flex items-center gap-2 px-3 py-2 border border-red-700 text-red-300 hover:bg-red-950/40 text-[11px] tracking-[0.2em] uppercase transition-colors disabled:opacity-50"
                    >
                        {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                        Delete this account
                    </button>
                    <p className="text-[10px] tracking-wider text-zinc-500 mt-2 leading-relaxed">
                        Erases the user + athletes + bookings + billing rows. Audit log entry retained.
                    </p>
                </div>
            )}

            <AssessmentFormModal
                open={!!assessmentAthlete}
                athlete={assessmentAthlete}
                currentStatus={STATUS_META[lead.status]?.label}
                onClose={() => setAssessmentAthlete(null)}
                onSaved={() => onChangeStatus?.(lead.status)}
            />

            {rescheduleOpen && lead.next_assessment && (
                <RescheduleAssessmentModal
                    requestId={lead.next_assessment.request_id}
                    parentName={fullName}
                    currentLabel={`${new Date(`${lead.next_assessment.slot.date}T00:00:00`).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })} · ${formatTimeDisplay(lead.next_assessment.slot.start_time)}`}
                    onClose={() => setRescheduleOpen(false)}
                    onDone={() => { setRescheduleOpen(false); onMetaChanged?.(); }}
                />
            )}

            {scheduleAssessOpen && (
                <ScheduleAssessmentModal
                    lead={lead}
                    onClose={() => setScheduleAssessOpen(false)}
                    onDone={() => { setScheduleAssessOpen(false); onMetaChanged?.(); }}
                />
            )}

            {chargeOpen && (
                <ChargeClientModal
                    userId={lead.user_id}
                    clientName={lead.name}
                    onClose={() => setChargeOpen(false)}
                    onCharged={() => onMetaChanged?.()}
                />
            )}

            {enrollOpen && (
                <EnrollmentLinkModal
                    parentUserId={lead.user_id}
                    onClose={() => setEnrollOpen(false)}
                />
            )}

            {editAccountOpen && isOwner && (
                <OwnerUserAdminModal
                    user={lead}
                    onClose={() => setEditAccountOpen(false)}
                    onChanged={(result) => {
                        setEditAccountOpen(false);
                        if (result?.deleted) {
                            onDeleted?.();
                            return;
                        }
                        onMetaChanged?.(result);
                    }}
                />
            )}
            {athleteEditor && (
                <AthleteEditorModal
                    mode={athleteEditor.mode}
                    athlete={athleteEditor.athlete}
                    clientUserId={lead.user_id}
                    onClose={() => setAthleteEditor(null)}
                    onSaved={() => onMetaChanged?.()}
                />
            )}
        </div>
    );
}


/**
 * CardOnFileChip — renders an emerald "Card on file" badge if the
 * client has a saved payment method at Stripe. Hits the live coach
 * payment-method-status endpoint so it's correct even when the cached
 * `card_on_file` flag is missing (e.g. pre-Setup-Intent-cache users like
 * Mike Krohn). The cached `initialCached` value is rendered immediately
 * to avoid a flash while the live call resolves.
 */
/**
 * EnterCardHeaderButton — one-tap card entry right by the client's name.
 * Opens Stripe Checkout (setup mode) in a new tab so the coach can type the
 * parent's card at the counter/on the phone. If the popup gets blocked or
 * the call fails, falls back to the Billing tab's card panel.
 */
function EnterCardHeaderButton({ userId, onFallback }) {
    const [busy, setBusy] = useState(false);
    const openCardEntry = async () => {
        setBusy(true);
        try {
            const r = await axios.post(
                `${API}/coach/clients/${userId}/setup-card`,
                { origin_url: window.location.origin, delivery: "open" },
                { withCredentials: true },
            );
            const win = window.open(r.data.url, "_blank", "noopener");
            if (!win) {
                toast.info("Popup blocked — use the card panel below", {
                    description: "The Billing tab is open with the card-entry buttons.",
                });
                onFallback?.();
            }
        } catch (e) {
            toast.error("Couldn't open card entry", { description: e?.response?.data?.detail });
            onFallback?.();
        } finally {
            setBusy(false);
        }
    };
    return (
        <button
            type="button"
            data-testid="lead-detail-enter-card"
            onClick={openCardEntry}
            disabled={busy}
            title="Enter a card for this client — opens Stripe's secure form in a new tab ($0 charged, card saved on file)"
            className="self-center inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase border border-emerald-600 bg-emerald-900/20 text-emerald-300 hover:bg-emerald-600 hover:text-black transition-colors px-2.5 py-1.5 rounded ml-1 disabled:opacity-50"
        >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CreditCard className="w-3.5 h-3.5" />}
            Enter card
        </button>
    );
}


function CardOnFileChip({ userId, initialCached, onAddCard }) {
    // Tri-state: "yes" | "no" | "unknown" — the add-card CTA only renders
    // once the live Stripe check confirms there is genuinely no card.
    const [state, setState] = useState(initialCached ? "yes" : "unknown");
    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const r = await axios.get(
                    `${API}/coach/clients/${userId}/payment-method-status`,
                    { withCredentials: true },
                );
                if (alive && r.data?.has_payment_method !== undefined) {
                    setState(r.data.has_payment_method ? "yes" : "no");
                }
            } catch {
                // Stale value is fine — fall back to the cached initial.
            }
        })();
        return () => { alive = false; };
    }, [userId]);

    if (state === "unknown") return null;
    if (state === "no") {
        return (
            <button
                type="button"
                data-testid="lead-detail-add-card-chip"
                onClick={onAddCard}
                title="No card on file — click to add one (open Stripe here or email the parent a link)"
                className="inline-flex items-center gap-1 text-[10px] tracking-[0.2em] uppercase border border-amber-600/70 bg-amber-900/20 text-amber-300 hover:bg-amber-600 hover:text-black transition-colors px-2 py-0.5"
            >
                <CreditCard className="w-3 h-3" />
                Add card
            </button>
        );
    }
    return (
        <span
            data-testid="lead-detail-card-on-file"
            className="inline-flex items-center gap-1 text-[10px] tracking-[0.2em] uppercase border border-emerald-600/60 bg-emerald-900/20 text-emerald-300 px-2 py-0.5"
            title="Card on file — coach can run charges without re-collecting"
        >
            <CreditCard className="w-3 h-3" />
            Card on file
        </span>
    );
}

